import { RefObject, useLayoutEffect, useRef, useState } from "react";

/** The CSS custom property the Analytics shell publishes its pinned header height under. */
export const ANALYTICS_HEADER_HEIGHT_VAR = "--analytics-header-height";

export interface StickyHeaderHeight {
	/** Attach to the pinned header wrapper. */
	ref: RefObject<HTMLDivElement>;
	/** Its current border-box height in px; 0 only before the first layout. */
	height: number;
}

/**
 * Measures the pinned page header so the filter bar below it can pin flush under it.
 *
 * Two sticky strips only stack when the second one's `top` equals the first one's height, and
 * that height is not a constant: the tab strip wraps on narrow viewports and the title bar's
 * actions vary per tab. The filter bar is rendered by each tab (twice in compare mode), so the
 * measurement travels as a CSS custom property rather than a prop.
 */
export const useStickyHeaderHeight = (): StickyHeaderHeight => {
	const ref = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState(0);

	useLayoutEffect(() => {
		const element = ref.current;

		if (!element) {
			return;
		}

		// Read synchronously in the layout phase: waiting for the observer's initial notification
		// leaves the bar pinned at the wrong offset for the first frames (the browser spec sees it).
		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		const measure = () => setHeight(element.offsetHeight);

		measure();

		const observer = new ResizeObserver(measure);

		observer.observe(element);

		return () => observer.disconnect();
	}, []);

	return { ref, height };
};
